import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ReactLenis } from "lenis/react";
import {
  Calendar,
  MapPin,
  ArrowRight,
  Rocket,
  Clock,
  Archive,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  getUpcomingEvents,
  getPastEvents,
} from "@/data/eventsData";

export default function Events() {
  const upcoming = getUpcomingEvents();
  const past = getPastEvents();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="relative min-h-screen bg-arjuna-ink" data-testid="events-page">
        <Navbar />

        {/* Hero */}
        <section className="relative overflow-hidden pt-32 pb-14">
          <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_55%_at_20%_20%,rgba(0,229,255,0.14),transparent_60%),radial-gradient(50%_45%_at_85%_30%,rgba(255,122,26,0.10),transparent_60%)]" />
          <div className="container-x relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl"
            >
              <span
                data-testid="events-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-arjuna-teal/30 bg-arjuna-teal/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.28em] text-arjuna-teal"
              >
                <Sparkles className="h-3.5 w-3.5" /> Community · Workshops · Meetups
              </span>
              <h1 className="mt-6 font-serif text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                Events at <span className="text-[#FF7A1A]">Arjuna Space</span>.
              </h1>
              <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
                Where builders, students, allies and future innovators gather  
                to see how orbit-scale data becomes on-ground impact.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Upcoming */}
        <section className="relative pb-16" data-testid="events-upcoming-section">
          <div className="container-x">
            <SectionHeader
              overline="Upcoming"
              title="Come build with us"
              icon={Rocket}
            />
            {upcoming.length === 0 ? (
              <EmptyState
                testid="upcoming-empty"
                text="Nothing on the launch pad right now. check back soon."
              />
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                {upcoming.map((e, i) => (
                  <EventCard key={e.slug} event={e} index={i} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Past */}
        <section className="relative border-t border-white/10 bg-arjuna-surface/40 py-16" data-testid="events-past-section">
          <div className="container-x">
            <SectionHeader
              overline="Past"
              title="From the archive"
              icon={Archive}
            />
            {past.length === 0 ? (
              <EmptyState
                testid="past-empty"
                text="Our archive is warming up. Past meetups and workshops will live here."
              />
            ) : (
              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {past.map((e, i) => (
                  <EventCard key={e.slug} event={e} index={i} compact />
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </ReactLenis>
  );
}

function SectionHeader({ overline, title, icon: Icon }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <p className="overline text-arjuna-teal">{overline}</p>
        <h2 className="mt-2 font-serif text-3xl text-white sm:text-4xl">{title}</h2>
      </div>
      <Icon className="h-6 w-6 text-arjuna-teal/70" />
    </div>
  );
}

function EmptyState({ text, testid }) {
  return (
    <div
      data-testid={testid}
      className="mt-8 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center text-sm text-slate-400"
    >
      {text}
    </div>
  );
}

function EventCard({ event, index, compact = false }) {
  const isUpcoming = event.status === "upcoming";
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`event-card-${event.slug}`}
    >
      <Link
        to={`/events/${event.slug}`}
        className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-arjuna-teal/40 hover:shadow-[0_30px_80px_-40px_rgba(0,229,255,0.5)]"
      >
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-black/50">
          <img
            src={event.posterUrl}
            alt={event.title}
            className="absolute inset-0 h-full w-full object-cover object-top opacity-90 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <span
              className="rounded-full border border-white/20 bg-black/50 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white backdrop-blur"
              style={{ borderColor: isUpcoming ? "rgba(0,229,255,0.4)" : "rgba(255,255,255,0.15)" }}
            >
              {isUpcoming ? "Upcoming" : "Past"}
            </span>
            {event.tags?.slice(0, compact ? 1 : 2).map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/15 bg-black/40 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-200 backdrop-blur"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <div className="p-5">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-arjuna-teal">
            <Calendar className="h-3.5 w-3.5" /> {event.dateLabel}
          </p>
          <h3 className="mt-2 font-serif text-xl text-white sm:text-2xl">
            {event.title}
          </h3>
          {!compact && (
            <p className="mt-2 line-clamp-2 text-sm text-slate-400">
              {event.summary}
            </p>
          )}
          <div className="mt-4 space-y-1 text-xs text-slate-400">
            <p className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-arjuna-teal" /> {event.venue} · {event.city}
            </p>
            {event.time && (
              <p className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-arjuna-teal" /> {event.time}
              </p>
            )}
          </div>

          <div className="mt-5 flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-arjuna-teal transition-transform group-hover:translate-x-0.5">
              View details <ArrowRight className="h-4 w-4" />
            </span>
            {isUpcoming && event.registerBy && (
              <span className="rounded-full border border-[#FF7A1A]/40 bg-[#FF7A1A]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#FF7A1A]">
                Register by {event.registerBy.split(",")[0]}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
